import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Users, Crown, Mail, Send, MapPin, DollarSign, CalendarCheck, TrendingUp, AlertTriangle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

function StatCard({ icon: Icon, label, value, sub, tone = "emerald", testId }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    blue: "bg-sky-50 text-sky-700 border-sky-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    violet: "bg-violet-50 text-violet-700 border-violet-200",
    pink: "bg-pink-50 text-pink-700 border-pink-200",
    zinc: "bg-zinc-50 text-zinc-700 border-zinc-200",
  };
  return (
    <Card className={`p-4 rounded-2xl border ${tones[tone]}`} data-testid={testId}>
      <div className="flex items-start gap-3">
        <Icon className="h-5 w-5 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide opacity-70 truncate">{label}</p>
          <p className="text-2xl font-heading font-bold leading-tight mt-0.5">{value}</p>
          {sub && <p className="text-[11px] opacity-70 mt-1">{sub}</p>}
        </div>
      </div>
    </Card>
  );
}

export default function AdminDashboardStats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [converting, setConverting] = useState(false);
  const [confirmConvert, setConfirmConvert] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/dashboard-stats");
      setStats(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement statistiques");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const convertWaitlist = async () => {
    setConverting(true);
    try {
      const { data } = await api.post("/admin/waitlist/convert-all");
      toast.success(
        `${data.created} compte(s) créé(s), ${data.invites_sent} email(s) envoyé(s). ${data.skipped_existing} déjà inscrit(s).`,
        { duration: 8000 }
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la conversion");
    } finally {
      setConverting(false);
      setConfirmConvert(false);
    }
  };

  if (loading) {
    return (
      <Card className="p-6 rounded-2xl border border-zinc-200 flex items-center gap-2 text-zinc-500 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Chargement du tableau de bord...
      </Card>
    );
  }
  if (!stats) return null;

  const u = stats.users;
  const h = stats.hosts;
  const w = stats.waitlist;
  const sp = stats.spaces;
  const b = stats.bookings;
  const rev = stats.revenue;

  return (
    <div className="space-y-4" data-testid="admin-dashboard-stats">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-lg font-bold text-zinc-950">Tableau de bord</h2>
          <p className="text-xs text-zinc-500">Vue d&apos;ensemble en temps réel</p>
        </div>
        <Button variant="ghost" size="sm" onClick={load} className="text-xs text-emerald-700 hover:text-emerald-800" data-testid="refresh-stats-btn">
          ↻ Actualiser
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        <StatCard icon={Users} tone="emerald" label="Utilisateurs" value={u.total} sub={`+${u.new_7d} cette semaine · +${u.new_30d} ce mois`} testId="stat-users" />
        <StatCard icon={Crown} tone="violet" label="Hôtes abonnés" value={h.active_subscriptions} sub={`${h.stripe_ready} Stripe prêt · ${h.grace_period} en grâce`} testId="stat-hosts" />
        <StatCard icon={Mail} tone="amber" label="Liste d'attente" value={w.total} sub={`+${w.new_7d} cette semaine`} testId="stat-waitlist" />
        <StatCard icon={MapPin} tone="blue" label="Terrains" value={sp.total} sub={`${sp.published} publiés`} testId="stat-spaces" />
        <StatCard icon={CalendarCheck} tone="pink" label="Réservations" value={b.total} sub={`${b.paid} payées · ${b.last_7d} cette semaine`} testId="stat-bookings" />
        <StatCard icon={TrendingUp} tone="emerald" label="Volume brut" value={`${rev.gross_volume_cad.toLocaleString("fr-CA", { minimumFractionDigits: 2 })} $`} sub={rev.currency} testId="stat-gmv" />
        <StatCard icon={DollarSign} tone="emerald" label="Commission VroumZy" value={`${rev.platform_fees_cad.toLocaleString("fr-CA", { minimumFractionDigits: 2 })} $`} sub="Total frais 3% encaissés" testId="stat-platform-fees" />
        <StatCard icon={Sparkles} tone="zinc" label="Réservations aujourd'hui" value={b.today} testId="stat-bookings-today" />
      </div>

      {w.total > 0 && (
        <Card className="p-4 rounded-2xl border border-amber-200 bg-amber-50" data-testid="waitlist-convert-cta">
          <div className="flex items-start gap-3 flex-wrap">
            <Send className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-zinc-900 text-sm">Convertir la liste d&apos;attente en comptes</p>
              <p className="text-xs text-zinc-600 mt-1">
                Crée un compte utilisateur pour chacun des <strong>{w.total}</strong> inscrit(s) et leur envoie un lien magique de connexion (valide 7 jours) par email. Les emails déjà inscrits sont ignorés.
              </p>
              <p className="text-[11px] text-amber-700 mt-2 flex items-start gap-1">
                <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" /> Nécessite SendGrid configuré pour envoyer les emails (sinon les comptes sont créés mais aucun email envoyé).
              </p>
            </div>
            <div className="shrink-0">
              {confirmConvert ? (
                <div className="flex gap-2 items-center">
                  <Button onClick={convertWaitlist} disabled={converting} size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl" data-testid="confirm-convert-waitlist-btn">
                    {converting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                    Oui, lancer ({w.total})
                  </Button>
                  <Button onClick={() => setConfirmConvert(false)} disabled={converting} size="sm" variant="ghost" className="text-zinc-600">
                    Annuler
                  </Button>
                </div>
              ) : (
                <Button onClick={() => setConfirmConvert(true)} size="sm" variant="outline" className="rounded-xl" data-testid="convert-waitlist-btn">
                  <Send className="h-3.5 w-3.5 mr-1" /> Convertir & envoyer
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
